'use client';

import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NAVIGATION_DROPDOWN_PANEL_CLASS, NAVIGATION_DROPDOWN_TRIGGER_CLASS } from './constants';
import type { NavigationDropDownProps } from './interfaces';

export const NavigationDropDown = ({
    label,
    ariaLabel,
    isActive,
    disabled,
    showChevron = true,
    containerClassName,
    triggerClassName,
    activeTriggerClassName = 'bg-overlay-white-30',
    inactiveTriggerClassName = 'bg-transparent hover:text-content-muted',
    panelClassName,
    panelLayout = 'navigation',
    panelPositionClassName,
    panelSizeClassName = 'min-w-[225px]',
    children,
}: NavigationDropDownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && event.target instanceof Node && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => document.removeEventListener('click', handleClickOutside);
    }, [isOpen]);

    const close = () => setIsOpen(false);
    const panelLayoutClass = panelLayout === 'floating' ? 'absolute z-1' : 'static max-lg:mt-2 lg:absolute lg:z-1';
    const resolvedPanelPositionClassName =
        panelPositionClassName ?? (panelLayout === 'floating' ? 'left-0' : undefined);

    return (
        <div ref={containerRef} className={cn('relative', containerClassName)}>
            <button
                type="button"
                aria-expanded={isOpen}
                aria-label={ariaLabel}
                disabled={disabled}
                className={cn(
                    NAVIGATION_DROPDOWN_TRIGGER_CLASS,
                    isActive ? activeTriggerClassName : inactiveTriggerClassName,
                    disabled && 'pointer-events-none opacity-60',
                    triggerClassName,
                )}
                onClick={() => setIsOpen((currentIsOpen) => !currentIsOpen)}
            >
                {label}
                {showChevron ? (
                    <ChevronDown
                        className={`ml-[10px] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                        size={24}
                    />
                ) : null}
            </button>

            <ul
                className={cn(
                    NAVIGATION_DROPDOWN_PANEL_CLASS,
                    panelSizeClassName,
                    panelLayoutClass,
                    resolvedPanelPositionClassName,
                    panelClassName,
                    isOpen ? 'block' : 'hidden',
                )}
            >
                {children(close)}
            </ul>
        </div>
    );
};
